import { AtomTagsProvider } from '@atoms/AtomTags/Context';
import { CodeSuggestionProvider } from '@common/CodeSuggestion';
import { useHistoricalViewID } from '@common/Configuration/hook';
import { useCopilotReadOnlyFlag } from '@common/Copilot/useCopilotReadonly';
import { ProjectEntityNotFoundContent } from '@common/EntityNotFound';
import { CodeGenStatusType } from '@common/enums';
import { getRefreshingMessage } from '@common/git/CodeGeneration/util';
import { useSpecs } from '@common/graph/gem-specs';
import { IDEModeProvider } from '@common/ide/IdeMode';
import { useIdeActions } from '@common/ide/state';
import { IDELoader } from '@common/IDELoader';
import { LanguageSpecProvider } from '@common/language-spec';
import { ManualCompilationProvider } from '@common/ManualCompilation';
import { useSelector } from '@common/performance/useSelector';
import { PipelineRunInfoType } from '@common/queries/pipeline';
import { useGraphMetaInfo } from '@common/selectors';
import { EditMode } from '@common/types';
import { Entity } from '@common/types/Entity';
import { useLSPHistory } from '@LSP/history/useLSPHistory';
import { ViewType } from '@LSP/types';
import { FROZEN_EMPTY_ARRAY, FROZEN_EMPTY_OBJECT } from '@prophecy/utils/constants';
import { memo, useEffect } from 'react';
import styled from 'styled-components';

import { useExecutionService } from '../../data/executionHooks';
import { WorkflowGraph, WorkflowState } from '../../redux/types';
import { SchemaProviderContext } from '../../schema/SchemaProvider';
import { ArrowTypeToSchemaTypeMap, ColumnToSchemaTypeMap, ProviderColumnTypes } from '../../schema/sqlTypes';
import { Providers } from '../../schema/types';
import { AttachFabricProvider } from './AttachFabric/AttachFabricProvider';
import { Canvas } from './Canvas';
import { useFunctionSuggestions } from './Functions/utils';
import { useCurrentFabric, useExecutionNotification } from './hook';
import { useAtomTags } from './hooks/useAtomTags';
import { PipelineFooter } from './PipelineFooter';

const CanvasContainer = styled.div`
  position: relative;
`;

export const Container = memo(
  ({
    initialize,
    uid,
    projectId,
    codeGenStatus,
    pipelineNotFound
  }: {
    uid: string;
    projectId: string;
    codeGenStatus?: CodeGenStatusType;
    pipelineNotFound?: boolean;
    initialize: (viewType: ViewType, jobData?: PipelineRunInfoType) => Promise<void>;
  }) => {
    const workflowId =
      useSelector<WorkflowState, string>((state) => state.metadata.workflowMeta?.workflowUID as string) || uid;
    const editMode = useSelector<WorkflowState, WorkflowState['editMode']>((state) => state.editMode);
    const gemSpecs = useSpecs();
    const isCopilotOnReadOnly = useCopilotReadOnlyFlag(false);

    const isManualCompilationEnabled = useGraphMetaInfo<WorkflowGraph, boolean>(
      (metaInfo) => metaInfo.pipelineSettingsInfo?.manualCompilationEnabled ?? false
    );

    const ideActions = useIdeActions();
    const executionService = useExecutionService();

    const pipelineRunId = useHistoricalViewID();
    const readOnly = editMode !== EditMode.enabled;
    useExecutionNotification(executionService, readOnly, !!pipelineRunId);

    useEffect(() => {
      ideActions.setReadOnly(readOnly);
    }, [readOnly, ideActions]);

    useLSPHistory();
    const { fabricId, providerType: fabricProvider } = useCurrentFabric();

    useEffect(() => {
      ideActions.setFabricId(fabricId);
    }, [fabricId, ideActions]);
    const { getTagCategories, renderAtomTagLabel } = useAtomTags();
    const suggestions = useFunctionSuggestions();
    const { setSourceSuggestions } = useIdeActions();
    useEffect(() => {
      // clean up source suggestions when unmounting (navigating away from pipeline)
      return () => {
        setSourceSuggestions(undefined);
      };
    }, [setSourceSuggestions]);

    const showCanvas = gemSpecs.length || (pipelineRunId && workflowId);
    return (
      <IDEModeProvider
        value={
          editMode === EditMode.enabled && !isCopilotOnReadOnly && !pipelineRunId ? EditMode.enabled : EditMode.disabled
        }>
        <SchemaProviderContext.Provider
          value={{
            providerType: Providers.Spark,
            providerColumnTypes: FROZEN_EMPTY_ARRAY as ProviderColumnTypes,
            columnToSchemaTypeMap: FROZEN_EMPTY_OBJECT as ColumnToSchemaTypeMap,
            arrowTypeToSchemaTypeMap: FROZEN_EMPTY_OBJECT as ArrowTypeToSchemaTypeMap
          }}>
          <AttachFabricProvider allowFabricSelection={true}>
            <CodeSuggestionProvider suggestions={suggestions}>
              <LanguageSpecProvider provider={Providers.Spark} fabricProvider={fabricProvider}>
                <AtomTagsProvider getTagCategories={getTagCategories} renderAtomTagLabel={renderAtomTagLabel}>
                  <ManualCompilationProvider isManualCompilationEnabled={isManualCompilationEnabled}>
                    <CanvasContainer>
                      {pipelineRunId ? null : <IDELoader tip={getRefreshingMessage({ status: codeGenStatus })} />}
                      {showCanvas ? (
                        <>
                          {pipelineNotFound ? (
                            <ProjectEntityNotFoundContent uid={uid} entity={Entity.Pipeline} projectId={projectId} />
                          ) : (
                            <Canvas workflowId={workflowId} initialize={initialize} />
                          )}
                        </>
                      ) : null}
                    </CanvasContainer>
                    {showCanvas ? (
                      // Note: keep footer parallel to canvas to avoid breaking grid layout
                      <PipelineFooter />
                    ) : null}
                  </ManualCompilationProvider>
                </AtomTagsProvider>
              </LanguageSpecProvider>
            </CodeSuggestionProvider>
          </AttachFabricProvider>
        </SchemaProviderContext.Provider>
      </IDEModeProvider>
    );
  }
);
